import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchQuery, graphql, useRelayEnvironment } from 'react-relay';
import { createOperationDescriptor, getRequest, type Disposable } from 'relay-runtime';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy.ts';
import { isEligibleForSoldPdpSponsoredYmalGrid } from './isEligibleForSoldPdpSponsoredYmalGrid.ts';

import {
    SOLD_PDP_YMAL_FEATURED_CAROUSEL_CAPACITY,
    SOLD_PDP_YMAL_ITEMS_PER_ROW,
    SOLD_PDP_YMAL_MAX_GRID_SPONSORED,
    SOLD_PDP_YMAL_SL_INITIAL,
    SOLD_PDP_YMAL_SL_SUBSEQUENT,
} from '../PdpRespSimilarItems/pdpRespSimilarItemsConstants.ts';

import { type PdpSharedSimilarItemsSponsoredLoadMoreQuery as LoadMoreQueryType } from './__generated__/PdpSharedSimilarItemsSponsoredLoadMoreQuery.graphql.ts';

export type GridSponsoredItem = NonNullable<
    NonNullable<LoadMoreQueryType['response']['viewer']['items']>[number]
>;

const loadMoreQuery = graphql`
    query PdpSharedSimilarItemsSponsoredLoadMoreQuery(
        $itemIds: [String]!
        $isTrade: Boolean!
        $priceBookName: String = ""
    ) {
        viewer {
            items: itemRead(itemIds: $itemIds) {
                ... on Item {
                    serviceId
                    title
                    browseUrl
                    categoryCode
                    ecommerceTrackingParams
                    ...PdpRespSimilarItemsGrid_similarItems
                    ...SharedRespRelatedItem_item @arguments(priceBookName: $priceBookName)
                    ...SharedMobileRelatedItem_item @arguments(priceBookName: $priceBookName)
                    ...FavoritesProvider_item
                    ...PdpSharedProductTileCarousel_items
                        @arguments(isTrade: $isTrade, priceBookName: $priceBookName)
                }
            }
        }
    }
`;

type UsePdpSharedItemsSponsoredLoadMoreArgs = {
    enabled: boolean;
    eligibleItemIds: readonly string[];
    isTrade: boolean;
    priceBookName: string;
    /** ID-pool offset after the initially inflated window. */
    initialOffset?: number;
    itemsPerRow?: number;
    subsequentCount?: number;
};

type UsePdpSharedItemsSponsoredLoadMoreResult = {
    extraGridItems: readonly GridSponsoredItem[];
    /** IDs past the grid cap, inflated for the featured carousel. */
    carouselLeftoverItems: readonly GridSponsoredItem[];
    /** Completed load-more pages, counted per request (not per returned item). */
    sponsoredPagesLoaded: number;
    hasNextSponsored: boolean;
    isLoadingNextSponsored: boolean;
    hasSponsoredLoadError: boolean;
    inflateNextSponsored: (count?: number) => Promise<void>;
};

function fullRowItemCount(count: number, itemsPerRow: number): number {
    return count - (count % itemsPerRow);
}

/** IDs past SOLD_PDP_YMAL_MAX_GRID_SPONSORED are reserved for the featured carousel. */
function remainingGridPoolIds(eligibleCount: number, offset: number): number {
    return Math.min(eligibleCount, SOLD_PDP_YMAL_MAX_GRID_SPONSORED) - offset;
}

export function usePdpSharedItemsSponsoredLoadMore({
    enabled,
    eligibleItemIds,
    isTrade,
    priceBookName,
    initialOffset = SOLD_PDP_YMAL_SL_INITIAL,
    itemsPerRow = SOLD_PDP_YMAL_ITEMS_PER_ROW,
    subsequentCount = SOLD_PDP_YMAL_SL_SUBSEQUENT,
}: UsePdpSharedItemsSponsoredLoadMoreArgs): UsePdpSharedItemsSponsoredLoadMoreResult {
    const environment = useRelayEnvironment();
    // offset and pagesLoaded are tracked separately from items.length: itemRead may return
    // fewer items than requested IDs (e.g. removed listings) and those IDs are not retried.
    const [loaded, setLoaded] = useState<{
        items: GridSponsoredItem[];
        offset: number;
        pagesLoaded: number;
    }>({
        items: [],
        offset: initialOffset,
        pagesLoaded: 0,
    });
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [carouselLeftoverItems, setCarouselLeftoverItems] = useState<GridSponsoredItem[]>([]);
    // ref, not state: sentinel can re-fire before state lands
    const loadingRef = useRef(false);
    const loadGenerationRef = useRef(0);
    const hasInitializedEligibleIdsRef = useRef(false);
    const eligibleItemIdsKey = eligibleItemIds.join(',');

    // fetchQuery only retains until the request completes; keep load-more results alive
    // for as long as this hook holds fragment refs in React state.
    const retainersRef = useRef<Disposable[]>([]);

    useEffect(() => {
        return () => {
            retainersRef.current.forEach(disposable => disposable.dispose());
            retainersRef.current = [];
        };
    }, []);

    // Login refetch replaces the Topsort pool; drop guest offset/items so the
    // next inflate slices the new ids from the initial window.
    useEffect(() => {
        if (!hasInitializedEligibleIdsRef.current) {
            hasInitializedEligibleIdsRef.current = true;
            return;
        }
        loadGenerationRef.current += 1;
        loadingRef.current = false;
        setLoaded({
            items: [],
            offset: initialOffset,
            pagesLoaded: 0,
        });
        setIsLoading(false);
        setHasError(false);
    }, [eligibleItemIdsKey, initialOffset]);

    const remainingIds = remainingGridPoolIds(eligibleItemIds.length, loaded.offset);
    const hasNextSponsored = enabled && remainingIds >= itemsPerRow;
    const leftoverCarouselIds = useMemo(() => {
        const carouselIds = eligibleItemIds.slice(
            SOLD_PDP_YMAL_MAX_GRID_SPONSORED,
            SOLD_PDP_YMAL_MAX_GRID_SPONSORED + SOLD_PDP_YMAL_FEATURED_CAROUSEL_CAPACITY
        );
        const isEligibleForDisplay = isEligibleForSoldPdpSponsoredYmalGrid({
            eligibleItemIdsCount: carouselIds.length,
            inflatedItemsCount: 0,
            itemsPerRow,
        });
        return isEligibleForDisplay ? carouselIds : [];
    }, [eligibleItemIds, itemsPerRow]);

    // if test is successful and carousel is still required): inflate carousel items
    // when user scrolls down, not on the page load
    useEffect(() => {
        let cancelled = false;

        if (!enabled || leftoverCarouselIds.length === 0) {
            setCarouselLeftoverItems([]);
        } else {
            const variables = { itemIds: [...leftoverCarouselIds], isTrade, priceBookName };
            retainersRef.current.push(
                environment.retain(createOperationDescriptor(getRequest(loadMoreQuery), variables))
            );

            void fetchQuery<LoadMoreQueryType>(environment, loadMoreQuery, variables)
                .toPromise()
                .then(data => {
                    if (cancelled) {
                        return;
                    }
                    setCarouselLeftoverItems(
                        (data?.viewer?.items || []).filter(filterFalsy) as GridSponsoredItem[]
                    );
                })
                .catch(() => {
                    if (!cancelled) {
                        setCarouselLeftoverItems([]);
                    }
                });
        }

        return (): void => {
            cancelled = true;
        };
    }, [enabled, environment, leftoverCarouselIds, isTrade, priceBookName]);

    const inflateNextSponsored = useCallback(
        async (count: number = subsequentCount): Promise<void> => {
            // Snap the request window to full grid rows: the merge discards partial
            // sponsored rows, so IDs past the last full row would be fetched for nothing.
            const remaining = remainingGridPoolIds(eligibleItemIds.length, loaded.offset);
            const pageSize = Math.min(count, fullRowItemCount(remaining, itemsPerRow));
            if (!enabled || loadingRef.current || pageSize < itemsPerRow) {
                return;
            }
            const generation = loadGenerationRef.current;
            loadingRef.current = true;
            setIsLoading(true);
            setHasError(false);

            // Topsort has no cursor API. Reuse the initial ranked ID pool and only inflate
            // this window; replace this workaround if vendor pagination becomes available.
            const pageItemIds = eligibleItemIds.slice(loaded.offset, loaded.offset + pageSize);
            const variables = { itemIds: pageItemIds, isTrade, priceBookName };
            retainersRef.current.push(
                environment.retain(createOperationDescriptor(getRequest(loadMoreQuery), variables))
            );
            try {
                const data = await fetchQuery<LoadMoreQueryType>(
                    environment,
                    loadMoreQuery,
                    variables
                ).toPromise();
                if (loadGenerationRef.current !== generation) {
                    return;
                }
                const pageItems = (data?.viewer?.items || []).filter(
                    filterFalsy
                ) as GridSponsoredItem[];
                setLoaded(prev => ({
                    items: [...prev.items, ...pageItems],
                    offset: prev.offset + pageItemIds.length,
                    pagesLoaded: prev.pagesLoaded + 1,
                }));
            } catch {
                if (loadGenerationRef.current === generation) {
                    setHasError(true);
                }
            } finally {
                if (loadGenerationRef.current === generation) {
                    loadingRef.current = false;
                    setIsLoading(false);
                }
            }
        },
        [
            enabled,
            environment,
            isTrade,
            loaded.offset,
            eligibleItemIds,
            priceBookName,
            itemsPerRow,
            subsequentCount,
        ]
    );

    return {
        extraGridItems: loaded.items,
        carouselLeftoverItems,
        sponsoredPagesLoaded: loaded.pagesLoaded,
        hasNextSponsored,
        isLoadingNextSponsored: enabled && isLoading,
        hasSponsoredLoadError: enabled && hasError,
        inflateNextSponsored,
    };
}
